import type {
  CalendarEvent,
  ConsultationFieldDefinition,
  ConsultationFieldValue,
  ConsultationTemplate,
} from './types/Calendar';
import { formatTime } from '../../shared/utils/localization';

export type StructuredConsultationField = ConsultationFieldDefinition & {
  sectionKey?: string;
  assignmentSortOrder: number;
  assignmentRequired: boolean;
};

type ConsultationFieldLike = {
  id?: string;
  name: string;
  fieldType: string;
  assignmentRequired?: boolean;
  isRequired?: boolean;
};

export const normalizeOptionalId = (value: string): string | undefined => {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
};

export const sortEventsByStart = (items: CalendarEvent[]): CalendarEvent[] => (
  items.sort((a, b) => new Date(a.startAtUtc).getTime() - new Date(b.startAtUtc).getTime())
);

export const resolveEventStaffIds = (event: { staffId?: string; staffIds?: string[] }): string[] => {
  if (event.staffIds && event.staffIds.length > 0) {
    return event.staffIds;
  }

  return event.staffId ? [event.staffId] : [];
};

export const resolveEventAssetIds = (event: { assetId?: string; assetIds?: string[] }): string[] => {
  if (event.assetIds && event.assetIds.length > 0) {
    return event.assetIds;
  }

  return event.assetId ? [event.assetId] : [];
};

export const getCalendarEventDisplayTitle = (
  event: Pick<CalendarEvent, 'title' | 'appointmentTypeId'>,
  appointmentTypeLabelById: Readonly<Record<string, string>>,
  untitledLabel: string,
): string => {
  const title = event.title?.trim();
  if (title) {
    return title;
  }

  const appointmentTypeId = event.appointmentTypeId?.trim();
  if (appointmentTypeId) {
    return appointmentTypeLabelById[appointmentTypeId]?.trim() || appointmentTypeId;
  }

  return untitledLabel;
};

export const formatTimeLabel = (value: string | Date): string => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return formatTime(parsed, { hour: '2-digit', minute: '2-digit' });
};

export const hasConsultationFieldValue = (fieldType: string, value: string | undefined): boolean => {
  if (fieldType === 'boolean') {
    return value === 'true' || value === 'false';
  }

  return Boolean(value?.trim());
};

export const getMissingRequiredConsultationFields = <TField extends ConsultationFieldLike>(
  fields: TField[],
  fieldValues: Record<string, string>,
): string[] => {
  return fields
    .filter((field) => (field.assignmentRequired ?? field.isRequired ?? false))
    .filter((field) => !hasConsultationFieldValue(field.fieldType, fieldValues[field.id ?? '']))
    .map((field) => field.name);
};

export const mergeStructuredConsultationFields = (
  template: ConsultationTemplate | null | undefined,
  definitions: ConsultationFieldDefinition[],
): StructuredConsultationField[] => {
  if (!template?.fields?.length) {
    return [];
  }

  const definitionsById = new Map(definitions.map((definition) => [definition.id, definition]));

  return template.fields
    .map((templateField): StructuredConsultationField | null => {
      const definition = definitionsById.get(templateField.fieldDefinitionId);
      if (!definition) {
        return null;
      }

      return {
        ...definition,
        sectionKey: templateField.sectionKey,
        assignmentSortOrder: templateField.sortOrder,
        assignmentRequired: templateField.isRequired,
      };
    })
    .filter((item): item is StructuredConsultationField => item !== null)
    .sort((left, right) => left.assignmentSortOrder - right.assignmentSortOrder);
};

export const buildStructuredConsultationFieldValues = (
  fields: StructuredConsultationField[],
  savedValues: ConsultationFieldValue[] = [],
): Record<string, string> => {
  const initialValues: Record<string, string> = {};

  fields.forEach((field) => {
    const saved = savedValues.find((value) => value.fieldDefinitionId === field.id);
    initialValues[field.id] = saved?.value ?? field.defaultValue ?? '';
  });

  return initialValues;
};
